import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AircraftModelSelect from '../../../components/AircraftModelSelect/AircraftModelSelect';
import TimePicker from '../../../components/TimePicker/TimePicker';
import aircraftService from '../../../services/aircraftService';

const AddAircraft = () => {
  const navigate = useNavigate();
  const { id, airlinerId } = useParams();
  
  // Check if being used by admin (URL contains /admin/)
  const isAdminMode = window.location.pathname.includes('/admin/');
  const adminAirlinerId = airlinerId; // In admin mode, airlinerId is in URL
  
  // Determine if edit mode:
  // - Admin: URL has /edit at end, id is aircraft ID
  // - Airliner: id exists and no /admin/ in URL
  const isEditMode = isAdminMode 
    ? window.location.pathname.includes('/edit')
    : !!id;
  const [formData, setFormData] = useState({
    aircraftModel: '',
    regNo: '',
    seats: '',
    endurance: '',
    flightNo: '',
    depAirport: '',
    etd: '',
    arrAirport: '',
    eta: '',
    belPrice: '',
    blPrice: '',
    seatPrice: '',
    flightTime: '',
    status: 'Scheduled',
    aircraftImages: [],
    existingImages: []
  });

  const [imagePreviews, setImagePreviews] = useState([]);
  const [fileInputs, setFileInputs] = useState([{ id: Date.now(), file: null, preview: null }]); // Track multiple file inputs with their files

  const [errors, setErrors] = useState({
    aircraftModel: false,
    regNo: false,
    seats: false,
    endurance: false
  });

  const [regNoExists, setRegNoExists] = useState(false);
  const [regNoExistsMessage, setRegNoExistsMessage] = useState('');
  const [touched, setTouched] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [loadingAircraft, setLoadingAircraft] = useState(false);
  const [originalRegNo, setOriginalRegNo] = useState(''); // Store original registration number
  const [isDraft, setIsDraft] = useState(true); // Track if aircraft is draft or published

  // Load aircraft data in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      loadAircraftData();
    }
  }, [isEditMode, id]);

  const loadAircraftData = async () => {
    setLoadingAircraft(true);
    try {
      const response = await aircraftService.getAircraftById(id);

      if (response.success && response.data) {
        // The aircraft data is nested under response.data.aircraft
        const aircraft = response.data.aircraft || response.data;
        const regNo = aircraft.registration_number || '';
        setOriginalRegNo(regNo); // Store original registration number
        setIsDraft(aircraft.is_draft === 1); // Set draft status

        setFormData({
          aircraftModel: aircraft.aircraftmodel
            ? `${aircraft.aircraftmodel.manaufacturer}/${aircraft.aircraftmodel.model}/${aircraft.aircraftmodel.type_designator}`
            : aircraft.aircraft_modual || '',
          regNo: regNo,
          seats: aircraft.seating_capacity || '',
          endurance: aircraft.endurance || '',
          flightNo: '',
          depAirport: '',
          etd: '',
          arrAirport: '',
          eta: '',
          belPrice: '',
          blPrice: '',
          seatPrice: '',
          flightTime: '',
          status: 'Scheduled',
          aircraftImages: [],
          existingImages: response.data.images || []
        });

        // No need to set previews for existing images as they have URLs in existingImages

        console.log('Form data set:', {
          aircraftModel: aircraft.aircraftmodel
            ? `${aircraft.aircraftmodel.manaufacturer}/${aircraft.aircraftmodel.model}/${aircraft.aircraftmodel.type_designator}`
            : aircraft.aircraft_modual || '',
          regNo: regNo,
          seats: aircraft.seating_capacity || '',
          endurance: aircraft.endurance || '',
          isDraft: aircraft.is_draft === 1
        });
      }
    } catch (error) {
      console.error('Error loading aircraft:', error);
      setSubmitError('Failed to load aircraft data. Please try again.');
    } finally {
      setLoadingAircraft(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Convert registration number to uppercase
    const processedValue = name === 'regNo' ? value.toUpperCase() : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Clear error when user starts typing
    if (touched && errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }

    // Clear registration exists error when user changes the value
    if (name === 'regNo' && regNoExists) {
      setRegNoExists(false);
      setRegNoExistsMessage('');
    }
  };

  const checkRegistrationExists = async (registrationNumber) => {
    if (!registrationNumber || registrationNumber.trim() === '') {
      setRegNoExists(false);
      setRegNoExistsMessage('');
      return;
    }

    try {
      const response = await aircraftService.checkRouteExists(registrationNumber);

      // Handle the unwrapped response from the interceptor
      if (response && response.exists && response.exists !== false) {
        setRegNoExists(true);
        setRegNoExistsMessage('This Registration Number is already in use.');
      } else {
        setRegNoExists(false);
        setRegNoExistsMessage('');
      }
    } catch (error) {
      // Don't block the user if the check fails
      setRegNoExists(false);
      setRegNoExistsMessage('');
    }
  };

  const handleRegNoBlur = () => {
    // Skip check if in edit mode and registration hasn't changed
    if (isEditMode && formData.regNo === originalRegNo) {
      setRegNoExists(false);
      setRegNoExistsMessage('');
      return;
    }
    checkRegistrationExists(formData.regNo);
  };

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      imagePreviews.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  const handleFileChange = (e, inputId) => {
    const file = e.target.files[0]; // Single file per input
    if (!file) return;

    // Update the specific input with its file and preview
    setFileInputs(prev => prev.map(input => {
      if (input.id === inputId) {
        return {
          ...input,
          file: file,
          preview: URL.createObjectURL(file)
        };
      }
      return input;
    }));

    // Add to formData
    setFormData(prev => ({
      ...prev,
      aircraftImages: [...prev.aircraftImages, file]
    }));

    // Reset input value so same file can be selected again
    e.target.value = '';
  };

  const handleAddFileInput = () => {
    // Maximum 5 file inputs allowed
    if (fileInputs.length < 5) {
      setFileInputs(prev => [...prev, { id: Date.now(), file: null, preview: null }]);
    }
  };

  const handleRemoveFileInput = (inputId) => {
    const inputToRemove = fileInputs.find(input => input.id === inputId);
    
    // Revoke preview URL if exists
    if (inputToRemove?.preview) {
      URL.revokeObjectURL(inputToRemove.preview);
    }

    // Remove from fileInputs
    if (fileInputs.length > 1) {
      setFileInputs(prev => prev.filter(input => input.id !== inputId));
      
      // Remove file from formData
      if (inputToRemove?.file) {
        setFormData(prev => ({
          ...prev,
          aircraftImages: prev.aircraftImages.filter(f => f !== inputToRemove.file)
        }));
      }
    }
  };

  const handleRemoveNewImage = (index) => {
    setFormData(prev => ({
      ...prev,
      aircraftImages: prev.aircraftImages.filter((_, i) => i !== index)
    }));

    URL.revokeObjectURL(imagePreviews[index]);
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = async (img) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await aircraftService.deleteImage({ imageData: img.id });
        setFormData(prev => ({
          ...prev,
          existingImages: prev.existingImages.filter(i => i.id !== img.id)
        }));
      } catch (error) {
        console.error('Failed to delete image:', error);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {
      aircraftModel: !formData.aircraftModel,
      regNo: !formData.regNo,
      seats: !formData.seats,
      endurance: !formData.endurance
    };
    setErrors(newErrors);
    setTouched(true);

    // Return true if no errors
    return !Object.values(newErrors).some(error => error);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (regNoExists) {
      return; // Don't submit if registration exists
    }
    if (validateForm()) {
      setIsSubmitting(true);
      setSubmitError('');
      try {
        // Prepare the data for submission
        const aircraftData = {
          aircraft_modual: formData.aircraftModel,
          registration_number: formData.regNo,
          seating_capacity: formData.seats,
          endurance: formData.endurance,
          hdn_draft_value: 0, // Not a draft
        };

        // Add user_id if in admin mode
        if (isAdminMode && adminAirlinerId) {
          aircraftData.user_id = parseInt(adminAirlinerId);
        }

        let result;
        if (isEditMode) {
          // Update existing aircraft
          result = await aircraftService.updateAircraft(id, aircraftData);
        } else {
          // Create new aircraft
          result = await aircraftService.createAircraft(aircraftData);
        }

        // result = {success: true, message: '...', data: {id: 152, ...}}
        if (result.success && result.data && result.data.id !== null && result.data.id !== undefined) {
          const aircraftId = result.data.id;

          // Upload images if any are selected
          if (formData.aircraftImages && formData.aircraftImages.length > 0) {
            try {
              await aircraftService.uploadImages(aircraftId, formData.aircraftImages);
              console.log('✅ Images uploaded successfully');
            } catch (imageError) {
              console.error('❌ Failed to upload images:', imageError);
              // Don't fail the whole operation if image upload fails
            }
          }

          // Show success message
          console.log('✅ Aircraft created successfully!');
          console.log('📍 isAdminMode:', isAdminMode);
          console.log('📍 adminAirlinerId:', adminAirlinerId);
          console.log('📍 aircraftId:', aircraftId);
          console.log('📍 Navigating to:', isAdminMode && adminAirlinerId 
            ? `/admin/airliners/${adminAirlinerId}/aircrafts`
            : `/airliner/aircraft/${aircraftId}/add-flight`
          );

          // Redirect based on mode
          if (isAdminMode && adminAirlinerId) {
            navigate(`/admin/airliners/${adminAirlinerId}/aircrafts`);
          } else {
            // Navigate to add flight page for airliner
            navigate(`/airliner/aircraft/${aircraftId}/add-flight`);
          }
        } else {
          console.error('Aircraft creation response:', result);
          setSubmitError(`Failed to ${isEditMode ? 'update' : 'create'} aircraft. Please try again.`);
          setIsSubmitting(false);
        }
      } catch (error) {
        setSubmitError(error.message || `Failed to ${isEditMode ? 'update' : 'create'} aircraft. Please try again.`);
        setIsSubmitting(false);
      }
    }
  };

  const handleCancel = () => {
    if (isAdminMode && adminAirlinerId) {
      navigate(`/admin/airliners/${adminAirlinerId}/aircrafts`);
    } else {
      navigate('/airliner/aircraft');
    }
  };

  const handleSave = async () => {
    if (regNoExists) {
      return; // Don't save if registration exists
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const aircraftData = {
        aircraft_modual: formData.aircraftModel,
        registration_number: formData.regNo,
        seating_capacity: formData.seats,
        endurance: formData.endurance || '00:00:00',
        hdn_draft_value: 1, // Save as draft
      };

      // Add user_id if in admin mode
      if (isAdminMode && adminAirlinerId) {
        aircraftData.user_id = parseInt(adminAirlinerId);
      }

      let response;

      if (isEditMode) {
        response = await aircraftService.updateAircraft(id, aircraftData);
      } else {
        response = await aircraftService.createAircraft(aircraftData);
      }

      if (response.success) {
        const aircraftId = response.data?.id || id;

        // Upload images if any are selected
        if (formData.aircraftImages && formData.aircraftImages.length > 0) {
          try {
            await aircraftService.uploadImages(aircraftId, formData.aircraftImages);
            console.log('✅ Images uploaded successfully');
          } catch (imageError) {
            console.error('❌ Failed to upload images:', imageError);
            // Don't fail the whole operation if image upload fails
          }
        }

        if (isAdminMode && adminAirlinerId) {
          navigate(`/admin/airliners/${adminAirlinerId}/aircrafts`);
        } else {
          navigate('/airliner/aircraft');
        }
      } else {
        setSubmitError('Failed to save draft. Please try again.');
        setIsSubmitting(false);
      }
    } catch (error) {
      setSubmitError(error.message || 'Failed to save draft. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleAddFlight = async () => {
    if (regNoExists) {
      return; // Don't proceed if registration exists
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Save aircraft changes first (keeping current draft status)
      const aircraftData = {
        aircraft_modual: formData.aircraftModel,
        registration_number: formData.regNo,
        seating_capacity: formData.seats,
        endurance: formData.endurance || '00:00:00',
        hdn_draft_value: isDraft ? 1 : 0, // Keep current draft status
      };

      const response = await aircraftService.updateAircraft(id, aircraftData);

      if (response.success) {
        // Upload images if any are selected
        if (formData.aircraftImages && formData.aircraftImages.length > 0) {
          try {
            await aircraftService.uploadImages(id, formData.aircraftImages);
            console.log('✅ Images uploaded successfully');
          } catch (imageError) {
            console.error('❌ Failed to upload images:', imageError);
            // Don't fail the whole operation if image upload fails
          }
        }

        // Navigate to add flight page
        navigate(`/airliner/aircraft/${id}/add-flight`);
      } else {
        setSubmitError('Failed to save aircraft changes. Please try again.');
        setIsSubmitting(false);
      }
    } catch (error) {
      setSubmitError(error.message || 'Failed to save aircraft changes. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-0 bg-jet-gray-100 min-h-screen w-full relative font-['Bahnschrift_Light','Segoe_UI',Tahoma,Geneva,Verdana,sans-serif]">
      <div className="py-5 px-[30px] flex justify-between items-center bg-transparent">
        <h1 className="m-0 text-lg text-[#333] font-bold mb-[7px] font-['Bahnschrift_Light']">{isEditMode ? 'Edit Aircraft' : 'Add Aircraft'}</h1>
        <button className="text-black h-9 py-2.5 px-5 text-[14px] font-['Bahnschrift',sans-serif] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-none rounded cursor-pointer font-medium transition-colors duration-200 inline-flex items-center gap-2 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]" onClick={handleCancel}>
          <i className="fa fa-arrow-left mr-2"></i> Back
        </button>
      </div>

      {loadingAircraft ? (
        <div className="flex justify-center items-center py-20">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd200]"></div>
            <p className="mt-4 text-gray-600">Loading aircraft data...</p>
          </div>
        </div>
      ) : (
        <div className="py-0 px-[30px] pb-[30px]">
          <form onSubmit={handleSubmit}>
            <section className="bg-white p-2.5 mb-5 rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
              <h2 className="inline-block text-base font-semibold m-0 leading-none mb-5 pb-2.5 border-gray-200 w-full font-['Bahnschrift_Light']">
                Aircraft Details
              </h2>

              <div className="grid grid-cols-2 gap-5 mb-5 last:mb-0 lg:grid-cols-2 md:grid-cols-1">
                <div className="flex flex-col">
                  <label className="inline-block max-w-full mb-1.5 font-bold text-[13px] text-[#333]">
                    Aircraft Model <span className="text-red-500 text-[8px] align-super ml-0.5">*</span>
                    <span className="relative inline-block ml-1">
                      <i
                        className="fa fa-info-circle text-gray-600 cursor-pointer text-xs"
                        onMouseEnter={() => setActiveTooltip('aircraftModel')}
                        onMouseLeave={() => setActiveTooltip(null)}
                      ></i>
                      {activeTooltip === 'aircraftModel' && (
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 py-2.5 px-3.5 bg-[#333] text-white text-[13px] font-normal whitespace-nowrap rounded-md z-[10000] pointer-events-none leading-[1.6] after:content-[''] after:absolute after:top-full after:left-1/2 after:-translate-x-1/2 after:border-[6px] after:border-transparent after:border-t-[#333]">
                          Manufacturer/ Model/ ICAO Type Designator
                        </span>
                      )}
                    </span>
                  </label>
                  <AircraftModelSelect
                    value={formData.aircraftModel}
                    onChange={(value) => handleChange({ target: { name: 'aircraftModel', value } })}
                    error={errors.aircraftModel}
                    required
                    disabled={isEditMode && !isDraft && !isAdminMode}
                  />
                  {errors.aircraftModel && (
                    <p className="text-xs text-red-500 mt-1">Please select the aircraft's Manufacturer/Model/ICAO Designator</p>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="inline-block max-w-full mb-1.5 font-bold text-[13px] text-[#333]">
                    Reg No. <span className="text-red-500 text-[8px] align-super ml-0.5">*</span>
                    <span className="relative inline-block ml-1">
                      <i
                        className="fa fa-info-circle text-gray-600 cursor-pointer text-xs"
                        onMouseEnter={() => setActiveTooltip('regNo')}
                        onMouseLeave={() => setActiveTooltip(null)}
                      ></i>
                      {activeTooltip === 'regNo' && (
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 py-2.5 px-3.5 bg-[#333] text-white text-[13px] font-normal whitespace-nowrap rounded-md z-[10000] pointer-events-none leading-[1.6] after:content-[''] after:absolute after:top-full after:left-1/2 after:-translate-x-1/2 after:border-[6px] after:border-transparent after:border-t-[#333]">
                          Registration Number
                        </span>
                      )}
                    </span>
                  </label>
                  <input
                    type="text"
                    name="regNo"
                    placeholder="REG NO."
                    value={formData.regNo}
                    onChange={handleChange}
                    onBlur={handleRegNoBlur}
                    required
                    readOnly={isEditMode && !isDraft && !isAdminMode}
                    className="w-full h-[34px] py-2 px-3 text-[13px] text-[#333] bg-white border border-[#ddd] rounded box-border transition-[border-color] duration-200 placeholder:text-[#999] focus:outline-none focus:border-[#ffd200] read-only:bg-[#f5f5f5] read-only:cursor-not-allowed"
                  />
                  {errors.regNo && (
                    <p className="text-xs text-red-500 mt-1">Please enter Aircraft Registration Number</p>
                  )}
                  {regNoExists && (
                    <p className="text-xs text-red-500 mt-1">{regNoExistsMessage}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-5 mb-5 last:mb-0 lg:grid-cols-2 md:grid-cols-1">
                <div className="flex flex-col">
                  <label className="inline-block max-w-full mb-1.5 font-bold text-[13px] text-[#333]">
                    Seat(s) <span className="text-red-500 text-[8px] align-super ml-0.5">*</span>
                    <span className="relative inline-block ml-1">
                      <i
                        className="fa fa-info-circle text-gray-600 cursor-pointer text-xs"
                        onMouseEnter={() => setActiveTooltip('seats')}
                        onMouseLeave={() => setActiveTooltip(null)}
                      ></i>
                      {activeTooltip === 'seats' && (
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 py-2.5 px-3.5 bg-[#333] text-white text-[13px] font-normal whitespace-nowrap rounded-md z-[10000] pointer-events-none leading-[1.6] after:content-[''] after:absolute after:top-full after:left-1/2 after:-translate-x-1/2 after:border-[6px] after:border-transparent after:border-t-[#333]">
                          Maximum Seating Capacity
                        </span>
                      )}
                    </span>
                  </label>
                  <input
                    type="number"
                    name="seats"
                    placeholder="Seat(s)"
                    value={formData.seats}
                    onChange={handleChange}
                    required
                    readOnly={isEditMode && !isDraft && !isAdminMode}
                    className="w-full h-[34px] py-2 px-3 text-[13px] text-[#333] bg-white border border-[#ddd] rounded box-border transition-[border-color] duration-200 placeholder:text-[#999] focus:outline-none focus:border-[#ffd200] read-only:bg-[#f5f5f5] read-only:cursor-not-allowed"
                  />
                  {errors.seats && (
                    <p className="text-xs text-red-500 mt-1">Please enter the Maximum Seating Capacity for this Aircraft</p>
                  )}
                </div>

                <div className="flex flex-col">
                  <label className="inline-block max-w-full mb-1.5 font-bold text-[13px] text-[#333]">
                    Endurance <span className="text-red-500 text-[8px] align-super ml-0.5">*</span>
                    <span className="relative inline-block ml-1">
                      <i
                        className="fa fa-info-circle text-gray-600 cursor-pointer text-xs"
                        onMouseEnter={() => setActiveTooltip('endurance')}
                        onMouseLeave={() => setActiveTooltip(null)}
                      ></i>
                      {activeTooltip === 'endurance' && (
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 py-2.5 px-3.5 bg-[#333] text-white text-[13px] font-normal whitespace-nowrap rounded-md z-[10000] pointer-events-none leading-[1.6] after:content-[''] after:absolute after:top-full after:left-1/2 after:-translate-x-1/2 after:border-[6px] after:border-transparent after:border-t-[#333]">
                          Endurance (HH:MM:SS format)
                        </span>
                      )}
                    </span>
                  </label>
                  <TimePicker
                    value={formData.endurance}
                    onChange={(value) => handleChange({ target: { name: 'endurance', value } })}
                    error={errors.endurance}
                    readOnly={isEditMode && !isDraft && !isAdminMode}
                    disabled={isSubmitting}
                  />
                  {errors.endurance && (
                    <p className="text-xs text-red-500 mt-1">Please enter Endurance</p>
                  )}
                </div>
              </div>
            </section>

            <section className="bg-white p-2.5 mb-5 rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
              <h2 className="inline-block text-base font-semibold m-0 leading-none mb-5 pb-2.5 border-gray-200 w-full font-['Bahnschrift_Light']">
                Aircraft Image
                <span className="relative inline-block ml-1">
                  <i
                    className="fa fa-info-circle text-gray-600 cursor-pointer text-sm"
                    onMouseEnter={() => setActiveTooltip('aircraftImage')}
                    onMouseLeave={() => setActiveTooltip(null)}
                  ></i>
                  {activeTooltip === 'aircraftImage' && (
                    <span className="absolute bottom-full left-auto right-[-10px] mb-2 py-2.5 px-3.5 bg-[#333] text-white text-[13px] font-normal whitespace-nowrap rounded-md z-[10000] pointer-events-none leading-[1.5] text-center after:content-[''] after:absolute after:top-full after:left-auto after:right-5 after:border-[6px] after:border-transparent after:border-t-[#333]">
                      {`Please Click on Browse button to select image then Click on Upload Photo button Photo Size Should be 1000x400`}
                    </span>
                  )}
                </span>
              </h2>

              {/* Multiple File Inputs - 3 per row with individual previews below each */}
              <div className="space-y-2.5">
                {Array.from({ length: Math.ceil(fileInputs.length / 3) }).map((_, rowIndex) => (
                  <div key={rowIndex}>
                    {/* File Input Row */}
                    <div className="flex items-start gap-1.5 mb-2.5">
                      {fileInputs.slice(rowIndex * 3, rowIndex * 3 + 3).map((input, colIndex) => {
                        const index = rowIndex * 3 + colIndex;
                        const isLast = index === fileInputs.length - 1;
                        const canAddMore = fileInputs.length < 5;
                        return (
                          <div key={input.id} className="flex items-center gap-1.5">
                            <input
                              type="file"
                              id={`aircraftImage-${input.id}`}
                              name="aircraftImage"
                              accept="image/*"
                              onChange={(e) => handleFileChange(e, input.id)}
                              className="hidden"
                            />
                            <div className="flex items-center w-[300px] bg-[#F7F7F7] border border-gray-300 rounded px-3 py-1">
                              <label
                                htmlFor={`aircraftImage-${input.id}`}
                                className="inline-block max-w-full mb-[5px] font-bold pt-[0.375rem] pb-[0.375rem] pl-4 pr-4 text-[13px] text-gray-700 bg-white border border-gray-400 rounded cursor-pointer transition-colors hover:bg-gray-50"
                              >
                                Choose file
                              </label>
                              <span className="ml-3 text-[13px] text-gray-500 flex-1 truncate">
                                {input.file ? input.file.name : 'No file chosen'}
                              </span>
                            </div>
                            {isLast && colIndex === 2 && canAddMore ? (
                              <button
                                type="button"
                                onClick={handleAddFileInput}
                                className="w-[35px] h-[35px] bg-[#2D4373] text-white border-none rounded font-bold text-lg cursor-pointer transition-colors flex items-center justify-center hover:bg-blue-800 flex-shrink-0"
                                title="Add another row"
                              >
                                +
                              </button>
                            ) : !isLast ? (
                              <button
                                type="button"
                                onClick={() => handleRemoveFileInput(input.id)}
                                className="w-[35px] h-[35px] bg-red-500 text-white border-none rounded font-bold text-lg cursor-pointer transition-colors flex items-center justify-center hover:bg-red-600 flex-shrink-0"
                                title="Remove this file input"
                              >
                                −
                              </button>
                            ) : null}
                          </div>
                        );
                      })}
                      {/* Add + button at the end of last row if it's the last item and not at position 2 */}
                      {rowIndex === Math.ceil(fileInputs.length / 3) - 1 && 
                       fileInputs.length % 3 !== 0 && 
                       fileInputs.length < 5 && (
                        <button
                          type="button"
                          onClick={handleAddFileInput}
                          className="w-[35px] h-[35px] bg-[#2D4373] text-white border-none rounded font-bold text-lg cursor-pointer transition-colors flex items-center justify-center flex-shrink-0"
                          title="Add another file input"
                        >
                          +
                        </button>
                      )}
                    </div>

                    {/* Preview Row - Below corresponding inputs */}
                    <div className="flex items-start gap-1.5 mb-4">
                      {fileInputs.slice(rowIndex * 3, rowIndex * 3 + 3).map((input) => (
                        <div key={`preview-${input.id}`} className="w-[300px]">
                          {input.preview && (
                            <div className="relative w-full h-[180px] border border-gray-200 rounded overflow-hidden shadow-sm">
                              <img
                                src={input.preview}
                                alt={input.file?.name || 'Preview'}
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveFileInput(input.id)}
                                className="absolute top-2 right-2 w-7 h-7 bg-[#ef4444] text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-red-600 transition-colors shadow-md z-10"
                                title="Remove image"
                              >
                                <i className="fa fa-times text-xs"></i>
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {fileInputs.length >= 5 && (
                  <p className="text-xs text-gray-500 mt-2">Maximum 5 images can be uploaded</p>
                )}
              </div>

              {/* Existing Images from Server */}
              {formData.existingImages.length > 0 && (
                <div className="mt-5">
                  <h3 className="text-sm font-semibold mb-3">Existing Images</h3>
                  <div className="grid grid-cols-3 gap-4">
                    {formData.existingImages.map((img) => (
                      <div key={img.id} className="relative w-full h-[180px] border border-gray-200 rounded overflow-hidden shadow-sm">
                        <img
                          src={img.presigned_url || img.aircraft_image}
                          alt="Aircraft"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveExistingImage(img)}
                          className="absolute top-2 right-2 w-7 h-7 bg-[#ef4444] text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-red-600 transition-colors shadow-md z-10"
                          title="Remove image"
                        >
                          <i className="fa fa-times text-xs"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* <section className="form-section">
            <h2 className="section-title">Flight Details</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label>
                  Flight No. <span className="required-icon">●</span>
                </label>
                <input
                  type="text"
                  name="flightNo"
                  placeholder="Flight Number"
                  value={formData.flightNo}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  Departure Airport <span className="required-icon">●</span>
                </label>
                <input
                  type="text"
                  name="depAirport"
                  placeholder="Departure Airport"
                  value={formData.depAirport}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  ETD <span className="required-icon">●</span>
                </label>
                <input
                  type="datetime-local"
                  name="etd"
                  value={formData.etd}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  Arrival Airport <span className="required-icon">●</span>
                </label>
                <input
                  type="text"
                  name="arrAirport"
                  placeholder="Arrival Airport"
                  value={formData.arrAirport}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  ETA <span className="required-icon">●</span>
                </label>
                <input
                  type="datetime-local"
                  name="eta"
                  value={formData.eta}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  BEL Price <span className="required-icon">●</span>
                </label>
                <input
                  type="number"
                  name="belPrice"
                  placeholder="BEL Price"
                  value={formData.belPrice}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  BL Price <span className="required-icon">●</span>
                </label>
                <input
                  type="number"
                  name="blPrice"
                  placeholder="BL Price"
                  value={formData.blPrice}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  Seat Price <span className="required-icon">●</span>
                </label>
                <input
                  type="number"
                  name="seatPrice"
                  placeholder="Seat Price"
                  value={formData.seatPrice}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  Flight Time <span className="required-icon">●</span>
                </label>
                <input
                  type="text"
                  name="flightTime"
                  placeholder="e.g., 2h 30m"
                  value={formData.flightTime}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  Status <span className="required-icon">●</span>
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  <option value="Scheduled">Scheduled</option>
                  <option value="Boarding">Boarding</option>
                  <option value="Departed">Departed</option>
                  <option value="Arrived">Arrived</option>
                  <option value="Cancelled">Cancelled</option>
                  <option value="Delayed">Delayed</option>
                </select>
              </div>
            </div>
          </section> */}

            {submitError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-5">
                {submitError}
              </div>
            )}

            <div className="flex justify-end gap-2.5 mt-[30px] px-[30px] md:flex-row flex-col md:px-[30px] px-0">
              {isEditMode ? (
                // Edit mode buttons
                <>
                  {isDraft ? (
                    // Draft aircraft: show "Add Aircraft" button
                    <button
                      type="submit"
                      disabled={regNoExists || isSubmitting}
                      className="text-black h-9 w-[156px] py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-none rounded cursor-pointer transition-all duration-200 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300] md:w-[156px] w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Adding...' : 'Add Aircraft'}
                    </button>
                  ) : (
                    // Published aircraft: show "Add Flight" button
                    <button
                      type="button"
                      disabled={isSubmitting}
                      className="text-black h-9 w-[156px] py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-none rounded cursor-pointer transition-all duration-200 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300] md:w-[156px] w-full disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleAddFlight}
                    >
                      {isSubmitting ? 'Saving...' : 'Add Flight'}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={isSubmitting}
                    className="text-black h-9 w-24 py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-none rounded cursor-pointer transition-all duration-200 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300] md:w-24 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleSave}
                  >
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </button>
                </>
              ) : (
                // Add mode buttons
                <>
                  <button
                    type="submit"
                    disabled={regNoExists || isSubmitting}
                    className="text-black h-9 w-[156px] py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-none rounded cursor-pointer transition-all duration-200 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300] md:w-[156px] w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Adding...' : 'Add Aircraft'}
                  </button>
                  <button
                    type="button"
                    disabled={regNoExists || isSubmitting}
                    className="text-black h-9 w-24 py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-none rounded cursor-pointer transition-all duration-200 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300] md:w-24 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleSave}
                  >
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </button>
                </>
              )}
              <button type="button" className="text-black h-9 w-24 py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-[#f4f4f4] text-[#444] border border-[#ddd] rounded cursor-pointer transition-all duration-200 hover:bg-[#e8e8e8] md:w-24 w-full" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AddAircraft;
